import { useCallback, useEffect, useState, type JSX } from 'react'
import { Award, BarChart3, Clock, GitCompare, Info, Loader2, Play, RefreshCw, Settings2, X } from 'lucide-react'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { Dropdown } from 'primereact/dropdown'
import { Section, DROPDOWN_CLASS, DROPDOWN_PANEL_CLASS } from '../../components/admin/ai/ui.js'
import { MODEL_ALIAS_OPTIONS } from '../../components/admin/ai/alias-options.js'
import EvalRunDetailDrawer from '../../components/admin/ai/EvalRunDetailDrawer.js'
import EvalRunCompareDrawer from '../../components/admin/ai/EvalRunCompareDrawer.js'
import SuiteManagerDrawer from '../../components/admin/ai/SuiteManagerDrawer.js'

interface EvalResultCase {
  caseId: string
  prompt: string
  expected: string
  actual: string
  passed: boolean
  score: number
  latencyMs: number
  error?: string
  actualTools?: string[]
  missingTools?: string[]
  unexpectedTools?: string[]
  argumentErrors?: Array<{ tool: string; errors: string[] }>
}

interface EvalRun {
  id: string
  suiteId: string
  suiteName: string
  status: 'running' | 'completed' | 'failed'
  alias: string
  totalCases: number
  passedCases: number
  failedCases: number
  accuracyRate: number
  avgLatencyMs: number
  totalTokens: number
  createdAt: string
  completedAt?: string
  results: EvalResultCase[]
}

interface Suite {
  id: string
  name: string
  description: string | null
  isSystem: boolean
  caseCount: number
}

export function AiEvaluationsContent(): JSX.Element {
  const toast = useToast()
  const [suites, setSuites] = useState<Suite[]>([])
  const [runs, setRuns] = useState<EvalRun[]>([])
  const [selectedRun, setSelectedRun] = useState<EvalRun | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isSuiteMgrOpen, setIsSuiteMgrOpen] = useState(false)
  const [compareMode, setCompareMode] = useState(false)
  const [compareSelection, setCompareSelection] = useState<EvalRun[]>([])
  const [isCompareOpen, setIsCompareOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [selectedSuiteId, setSelectedSuiteId] = useState('default_benchmark')
  const [selectedAlias, setSelectedAlias] = useState('reasoning_premium')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<{ suites: Suite[]; runs: EvalRun[] }>('/admin/ai/evaluations')
      setSuites(res.suites ?? [])
      setRuns(res.runs ?? [])
    } catch (err) {
      toast.error('Failed to load evaluation data', String(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    void load()
  }, [])

  async function triggerRun(): Promise<void> {
    setRunning(true)
    try {
      const res = await api.post<EvalRun>('/admin/ai/evaluations/run', {
        suiteId: selectedSuiteId,
        alias: selectedAlias,
      })
      toast.success('Evaluation run completed', `Accuracy: ${res.accuracyRate}% across ${res.totalCases} cases`)
      // Reload rather than prepending — the API prunes older runs once the
      // retention cap is exceeded, so local state can drift without a refetch.
      void load()
      setSelectedRun(res)
      setIsDetailOpen(true)
    } catch (err) {
      toast.error('Evaluation run failed', String(err))
    } finally {
      setRunning(false)
    }
  }

  function handleSelectRun(r: EvalRun): void {
    setSelectedRun(r)
    setIsDetailOpen(true)
  }

  function toggleCompareSelection(r: EvalRun): void {
    setCompareSelection((prev) => {
      const exists = prev.some((p) => p.id === r.id)
      if (exists) return prev.filter((p) => p.id !== r.id)
      if (prev.length >= 2) return [prev[1]!, r] // replace oldest
      return [...prev, r]
    })
  }

  function openCompare(): void {
    if (compareSelection.length !== 2) return
    setIsCompareOpen(true)
  }

  function exitCompareMode(): void {
    setCompareMode(false)
    setCompareSelection([])
  }

  return (
    <>
      {/* Runner Config Card */}
      <Section
        icon={<Award className="h-4 w-4" />}
        title="Run Benchmark Test"
        description="Select a test suite and model alias to run live assertion benchmarks."
        action={
          <RequirePermission permission="admin:extensions:manage" fallback={null}>
            <button
              type="button"
              onClick={() => setIsSuiteMgrOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Manage Suites
            </button>
          </RequirePermission>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Evaluation Suite
            </label>
            <Dropdown
              value={selectedSuiteId}
              options={suites.map((s) => ({ label: `${s.name} (${s.caseCount} cases)`, value: s.id }))}
              optionLabel="label"
              optionValue="value"
              onChange={(e: { value: string }) => setSelectedSuiteId(e.value)}
              className={DROPDOWN_CLASS}
              panelClassName={DROPDOWN_PANEL_CLASS}
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Target Model Alias
            </label>
            <Dropdown
              value={selectedAlias}
              options={[...MODEL_ALIAS_OPTIONS]}
              optionLabel="label"
              optionValue="value"
              onChange={(e: { value: string }) => setSelectedAlias(e.value)}
              className={DROPDOWN_CLASS}
              panelClassName={DROPDOWN_PANEL_CLASS}
            />
          </div>
          <div className="flex items-end">
            <RequirePermission permission="admin:extensions:manage" fallback={null}>
              <button
                type="button"
                onClick={() => void triggerRun()}
                disabled={running}
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
              >
                {running ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Run Evaluation
              </button>
            </RequirePermission>
          </div>
        </div>
      </Section>

      {/* Full-width run list */}
      <Section
        icon={<BarChart3 className="h-4 w-4" />}
        title="Recent Evaluation Runs"
        description="Each run executes a suite's cases against one model alias and reports pass/fail accuracy."
        action={
          <div className="flex items-center gap-2">
            <span
              className="group relative inline-flex cursor-help items-center text-gray-400 hover:text-gray-600"
              aria-label="Runs retention info"
            >
              <Info className="h-3.5 w-3.5" />
              <span className="pointer-events-none absolute right-0 top-full z-10 mt-1 hidden w-56 rounded-sm border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-relaxed text-amber-800 shadow-sm group-hover:block">
                Only the latest 50 runs are stored — older runs are deleted automatically when a
                new run completes.
              </span>
            </span>
            {runs.length >= 2 &&
            (compareMode ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{compareSelection.length}/2 selected</span>
                <button
                  type="button"
                  onClick={openCompare}
                  disabled={compareSelection.length !== 2}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-40"
                >
                  <GitCompare className="h-3.5 w-3.5" />
                  Compare
                </button>
                <button
                  type="button"
                  onClick={exitCompareMode}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-2.5 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setCompareMode(true); setCompareSelection([]) }}
                className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                <GitCompare className="h-3.5 w-3.5" />
                Compare runs
              </button>
            ))}
          </div>
        }
      >
        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-gray-300" />
            <p className="mt-2 text-xs text-gray-400">Loading evaluation history…</p>
          </div>
        ) : runs.length === 0 ? (
          <div className="py-8 text-center">
            <BarChart3 className="mx-auto mb-2 h-8 w-8 text-gray-300" />
            <p className="text-sm font-medium text-gray-600">No benchmark runs recorded yet.</p>
            <p className="mt-1 text-xs text-gray-400">Run a benchmark to see results here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {runs.map((r) => {
              const isSelected = compareSelection.some((s) => s.id === r.id)
              const selectionIdx = compareSelection.findIndex((s) => s.id === r.id)
              return (
                <button
                  key={r.id}
                  onClick={() => (compareMode ? toggleCompareSelection(r) : handleSelectRun(r))}
                  className={`flex flex-col gap-2 rounded-sm border p-4 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3454cf] ${
                    compareMode && isSelected
                      ? 'border-[#3454cf] bg-[#3454cf]/[0.03] ring-1 ring-[#3454cf]/30'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold text-gray-900">{r.suiteName}</span>
                    {compareMode && isSelected && (
                      <span className="shrink-0 rounded-sm bg-[#3454cf] px-1.5 py-0.5 text-[10px] font-bold text-white">
                        {selectionIdx === 0 ? 'A' : 'B'}
                      </span>
                    )}
                    <span
                      className={`shrink-0 rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${
                        r.accuracyRate >= 90
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-amber-50 text-amber-700'
                      }`}
                    >
                      {r.accuracyRate}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-gray-500">
                    <span className="font-mono">{r.alias}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                    <span className={`h-2 w-2 rounded-full ${r.passedCases === r.totalCases ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    {r.passedCases} / {r.totalCases} passed
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </Section>

      <EvalRunDetailDrawer
        run={selectedRun}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />

      <EvalRunCompareDrawer
        runA={compareSelection[0] ?? null}
        runB={compareSelection[1] ?? null}
        isOpen={isCompareOpen}
        onClose={() => setIsCompareOpen(false)}
      />

      <SuiteManagerDrawer
        isOpen={isSuiteMgrOpen}
        onClose={() => setIsSuiteMgrOpen(false)}
        onMutated={() => void load()}
      />
    </>
  )
}

export default function AiEvaluationsPage(): JSX.Element {
  return (
    <RequirePermission permission="admin:extensions:view">
      <div className="space-y-5">
        <AiEvaluationsContent />
      </div>
    </RequirePermission>
  )
}
